import type { GridColDef } from '@mui/x-data-grid-pro';
import type { GridInitialStatePro } from '@mui/x-data-grid-pro/models/gridStatePro';

// Only what a resize writes: minWidth/maxWidth are left to the def.
export interface ColumnDimension {
  width: number;
  flex?: number;
}

export type ColumnDimensionMap = Record<string, ColumnDimension>;

const SERIALIZED_INFINITY = -1;

const isUsableWidth = (width: unknown): width is number =>
  typeof width === 'number' && width > 0 && width !== SERIALIZED_INFINITY;

// Every column arrives here, not only dragged ones: MUI flags any column whose def declares a width as
// resized, and the grid saves the whole export on unmount. So once a user has opened a grid, a later change
// to a def's width no longer reaches them, and there is no reset — telling the two apart needs the saved
// shape to record which widths the user actually chose.
export const readSavedColumnDimensions = (state: GridInitialStatePro | null | undefined): ColumnDimensionMap => {
  const saved = state?.columns?.dimensions || {};

  return Object.entries(saved).reduce<ColumnDimensionMap>((dimensions, [field, { width, flex }]) => {
    if (isUsableWidth(width)) dimensions[field] = flex === undefined ? { width } : { width, flex };

    return dimensions;
  }, {});
};

// flex 0, because a drag pins the column: MUI's own resize clears flex before it reports the new width.
export const recordColumnWidth = (
  dimensions: ColumnDimensionMap,
  field: string,
  width: number
): ColumnDimensionMap => ({ ...dimensions, [field]: { width, flex: 0 } });

export const applyColumnDimensions = <T extends GridColDef>(
  columns: readonly T[],
  dimensions: ColumnDimensionMap
): T[] => columns.map((column) => (dimensions[column.field] ? { ...column, ...dimensions[column.field] } : column));

export const withColumnDimensions = (
  state: GridInitialStatePro,
  dimensions: ColumnDimensionMap
): GridInitialStatePro => ({
  ...state,
  columns: { ...state.columns, dimensions: { ...state.columns?.dimensions, ...dimensions } }
});

// Which drags the saved state has not caught up with yet. An entry that disagrees on flex counts as
// unsaved: a false match would drop both the width and the unload warning that protects it.
export const unsavedDimensions = (dragged: ColumnDimensionMap, saved: ColumnDimensionMap): ColumnDimensionMap =>
  Object.entries(dragged).reduce<ColumnDimensionMap>((unsaved, [field, dimension]) => {
    const landed = saved[field]?.width === dimension.width && saved[field]?.flex === dimension.flex;
    if (!landed) unsaved[field] = dimension;

    return unsaved;
  }, {});
